import React, { lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { AdminAuthProvider } from './contexts/AdminAuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import AdminSessionGuard from './components/AdminSessionGuard'
import PWAInstallPrompt from './components/PWAInstallPrompt'
import CustomCSSLoader from './components/CustomCSSLoader'
import Header from './components/Header'
import Footer from './components/Footer'

// Critical pages - Load immediately (First Paint)
import Home from './pages/Home'
import Tours from './pages/Tours'

// Non-critical pages - Lazy load (Code splitting)
const TourDetail = lazy(() => import('./pages/TourDetail'))
const Blog = lazy(() => import('./pages/Blog'))
const BlogDetail = lazy(() => import('./pages/BlogDetail'))
const Destinations = lazy(() => import('./pages/Destinations'))
const About = lazy(() => import('./pages/About'))
const Contact = lazy(() => import('./pages/Contact'))
const Terms = lazy(() => import('./pages/Terms'))
const Policy = lazy(() => import('./pages/Policy'))
const FAQ = lazy(() => import('./pages/FAQ'))
const Booking = lazy(() => import('./pages/Booking'))
const Login = lazy(() => import('./pages/Login'))
const Register = lazy(() => import('./pages/Register'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const Profile = lazy(() => import('./pages/Profile'))
const MyBookings = lazy(() => import('./pages/MyBookings'))
const AdminRoutes = lazy(() => import('./pages/admin/AdminRoutes'))

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
      <p className="text-gray-600 font-medium">Đang tải...</p>
    </div>
  </div>
)

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AdminAuthProvider>
          <CustomCSSLoader />
          <div className="min-h-screen flex flex-col">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Admin Routes - NO guard (needs admin session) */}
                <Route path="/admin/*" element={<AdminRoutes />} />

                {/* Customer Routes - WITH guard (prevents admin session leak) */}
                <Route path="/*" element={
                  <AdminSessionGuard>
                    <Routes>
                      <Route path="/" element={<><Header /><main className="flex-1"><Home /></main><Footer /></>} />
                      <Route path="/tours" element={<><Header /><main className="flex-1"><Tours /></main><Footer /></>} />
                      <Route path="/tours/:id" element={<><Header /><main className="flex-1"><TourDetail /></main><Footer /></>} />
                      <Route path="/blog" element={<><Header /><main className="flex-1"><Blog /></main><Footer /></>} />
                      <Route path="/blog/:id" element={<><Header /><main className="flex-1"><BlogDetail /></main><Footer /></>} />
                      <Route path="/destinations" element={<><Header /><main className="flex-1"><Destinations /></main><Footer /></>} />
                      <Route path="/about" element={<><Header /><main className="flex-1"><About /></main><Footer /></>} />
                      <Route path="/contact" element={<><Header /><main className="flex-1"><Contact /></main><Footer /></>} />
                      <Route path="/terms" element={<><Header /><main className="flex-1"><Terms /></main><Footer /></>} />
                      <Route path="/policy" element={<><Header /><main className="flex-1"><Policy /></main><Footer /></>} />
                      <Route path="/faq" element={<><Header /><main className="flex-1"><FAQ /></main><Footer /></>} />
                      <Route path="/booking/:tourId" element={<><Header /><main className="flex-1"><Booking /></main><Footer /></>} />
                      <Route path="/login" element={<><Header /><main className="flex-1"><Login /></main><Footer /></>} />
                      <Route path="/register" element={<><Header /><main className="flex-1"><Register /></main><Footer /></>} />
                      <Route path="/forgot-password" element={<><Header /><main className="flex-1"><ForgotPassword /></main><Footer /></>} />
                      <Route path="/profile" element={<><Header /><main className="flex-1"><Profile /></main><Footer /></>} />
                      <Route path="/my-bookings" element={<><Header /><main className="flex-1"><MyBookings /></main><Footer /></>} />
                    </Routes>
                  </AdminSessionGuard>
                } />
              </Routes>
            </Suspense>
            <PWAInstallPrompt />
          </div>
        </AdminAuthProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App
